import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { Copy, Danger, Refresh, TickCircle } from 'iconsax-reactjs';
import { Button } from '../Button/Button';
import { cx } from '../internal/cx';
import styles from './ErrorState.module.css';

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  message?: ReactNode;
  /** Server correlation id; shown as copyable "Reference: …" text for support requests. */
  requestId?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
  /** Announce the error to screen readers (role="alert"). Default true. */
  announce?: boolean;
  size?: 'sm' | 'md';
  titleAs?: 'h2' | 'h3' | 'h4' | 'p';
}

export function ErrorState({
  title = 'Something went wrong',
  message = 'We could not load this information. Please try again.',
  requestId,
  onRetry,
  retryLabel = 'Try again',
  retrying = false,
  announce = true,
  size = 'md',
  titleAs: Title = 'h3',
  className,
  children,
  ...rest
}: ErrorStateProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  async function copyReference() {
    if (!requestId) return;
    try {
      await navigator.clipboard.writeText(requestId);
      setCopied(true);
      window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be blocked; the reference remains selectable text.
    }
  }

  return (
    <div
      className={cx(styles.root, styles[size], className)}
      role={announce ? 'alert' : undefined}
      {...rest}
    >
      <span className={styles.icon} aria-hidden="true">
        <Danger size={24} color="currentColor" />
      </span>
      <Title className={styles.title}>{title}</Title>
      {message && <p className={styles.message}>{message}</p>}
      {requestId && (
        <p className={styles.reference}>
          <span>
            Reference: <code className={styles.code}>{requestId}</code>
          </span>
          <button
            type="button"
            className={styles.copy}
            onClick={() => void copyReference()}
            aria-label={copied ? 'Reference copied' : 'Copy reference'}
          >
            {copied ? (
              <TickCircle size={14} color="currentColor" aria-hidden="true" />
            ) : (
              <Copy size={14} color="currentColor" aria-hidden="true" />
            )}
          </button>
        </p>
      )}
      {children}
      {onRetry && (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Refresh size={16} color="currentColor" />}
            onClick={onRetry}
            loading={retrying}
          >
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
